/**
 * Haptics（触覚フィードバック・R2 Wave3B・2026-06-21）
 *
 * `navigator.vibrate` を progressive enhancement として安全に叩く小モジュール。
 * 音（audioEngine）と対になる「感覚層」の振動側。semantic の FailOverlay /
 * JudgementModal が「結果が出た瞬間」に短く震わせるために使う。
 *
 * 設計上の肝:
 *  - **feature-detect 必須**: navigator.vibrate は Android Chrome 等にしか無い。
 *    iOS Safari / Desktop の多くは非対応 → その場合は何もしない（= 正しい挙動）。
 *  - **SSR / 非ブラウザ安全**: typeof navigator/window のガードで throw しない。
 *  - **例外を握りつぶす**: 一部ブラウザは backgrounded タブ等で vibrate が throw する
 *    ことがある。振動は付加機能なので、失敗しても呼び出し側の描画を巻き込まない。
 *  - **ユーザー設定**: localStorage `mg-haptics-enabled`（既定 true）で ON/OFF。
 *    OFF の間は vibrate を一切呼ばない。OFF にすると進行中の振動も停止する。
 *
 * 注意: vibrate はユーザー操作（タップ等）由来でないと一部環境で無視される。
 * semantic 側の発火は「成功/失敗の確定 → 描画」という、直前のユーザー操作に
 * 連なる文脈で起きるため、実機でも概ね発火する。発火しなくても無害。
 */

/** localStorage キー: ハプティクスの ON/OFF を永続化する。 */
const HAPTICS_ENABLED_KEY = 'mg-haptics-enabled';

/**
 * 短いプリセット（ミリ秒。数値=1 回振動 / 配列=[振動, 休止, 振動, …]）。
 * 名前は SfxName（success/fail/tap）と対応させ、音と振動を読み手が紐づけやすくする。
 *  - success: ごく短い単発（「通った」の手応え）
 *  - fail:    2 連の重め（「弾かれた」感触）
 *  - tap:     最小の単発（操作確定の軽いコツン）
 */
export const HAPTIC_PATTERNS = {
  success: [12],
  fail: [30, 40, 30],
  tap: [8],
} as const;

export type HapticPatternName = keyof typeof HAPTIC_PATTERNS;

/** navigator.vibrate が使える環境か（feature-detect・SSR 安全）。 */
function isVibrateSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    typeof navigator.vibrate === 'function'
  );
}

/**
 * ハプティクスが有効か（localStorage 既定 true）。
 * localStorage 不在（SSR・プライベートモード等）の場合は既定の true を返す。
 */
export function isHapticsEnabled(): boolean {
  try {
    if (typeof localStorage === 'undefined') return true;
    return localStorage.getItem(HAPTICS_ENABLED_KEY) !== 'false';
  } catch {
    // localStorage アクセスが throw する環境（厳格な privacy 設定等）でも既定 true。
    return true;
  }
}

/**
 * ハプティクスの ON/OFF を設定（localStorage 永続）。
 * OFF にした場合は進行中の振動も止める（vibrate(0)）。
 */
export function setHapticsEnabled(enabled: boolean): void {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(HAPTICS_ENABLED_KEY, enabled ? 'true' : 'false');
    }
  } catch {
    // 永続化に失敗しても致命ではない（このセッションの呼び出しは下の停止のみ反映）。
  }
  if (!enabled && isVibrateSupported()) {
    try {
      navigator.vibrate(0);
    } catch {
      /* 振動停止の失敗は無視 */
    }
  }
}

/**
 * 振動させる（feature-detect + ユーザー設定 + 例外ガード込み）。
 * 非対応・OFF・例外いずれでも安全に no-op。
 *
 * @param pattern 単一ミリ秒、または [振動, 休止, …] の配列。
 */
export function vibrate(pattern: number | number[]): void {
  if (!isVibrateSupported()) return;
  if (!isHapticsEnabled()) return;
  try {
    navigator.vibrate(pattern);
  } catch {
    // backgrounded タブ等で稀に throw する。付加機能なので握りつぶす。
  }
}

/**
 * プリセット名で振動させるショートカット。
 * semantic からは `vibratePreset('fail')` のように使う。
 */
export function vibratePreset(name: HapticPatternName): void {
  // スプレッドで mutable number[] 化（HAPTIC_PATTERNS は readonly tuple のため）。
  vibrate([...HAPTIC_PATTERNS[name]]);
}
